import { Outlet, NavLink } from 'react-router-dom';
import { Calendar, Clock, Plus, UserCheck, Pill, LogOut, CalendarRange, Bell, X } from 'lucide-react';
import { useCurrentUser, useLogout } from '@/api/auth';
import { useMyNotifications, useMarkNotificationsRead } from '@/api/resources';

const items = [
  { to: '/my/week', label: 'Ma semaine', icon: Calendar },
  { to: '/my/availability', label: 'Mes disponibilités', icon: CalendarRange },
  { to: '/my/hours', label: 'Mes heures', icon: Clock },
  { to: '/my/leaves', label: 'Mes congés', icon: Plus },
  { to: '/my/history', label: 'Historique', icon: Bell },
  { to: '/my/profile', label: 'Mon profil', icon: UserCheck },
];

export default function SalarieLayout() {
  const { data: user } = useCurrentUser();
  const logout = useLogout();
  const { data: notif } = useMyNotifications();
  const unreadCount = notif?.unreadCount ?? 0;

  return (
    <div className="w-full h-screen bg-slate-50 flex flex-col">
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-accent flex items-center justify-center">
            <Pill size={14} color="#fff" strokeWidth={2.25} />
          </div>
          <div className="text-[13px] font-semibold text-slate-900">
            Officina <span className="text-[10px] font-normal text-slate-500 ml-1">— Espace salarié</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[12px] font-medium text-slate-900">
              {user?.employee?.firstName} {user?.employee?.lastName}
            </div>
            <div className="text-[10px] text-slate-500">{user?.pharmacy?.name}</div>
          </div>
          <button
            onClick={logout}
            className="p-1.5 hover:bg-slate-100 rounded-lg"
            title="Se déconnecter"
          >
            <LogOut size={14} className="text-slate-500" />
          </button>
        </div>
      </div>

      <div className="bg-white border-b border-slate-200 px-6 flex gap-0.5">
        {items.map((t) => {
          const Icon = t.icon;
          return (
            <NavLink
              key={t.to}
              to={t.to}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3.5 py-3 text-[12.5px] font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-slate-500 hover:text-slate-900'
                }`
              }
            >
              <Icon size={13} />
              {t.label}
              {t.to === '/my/history' && unreadCount > 0 && (
                <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-accent text-white text-[9.5px] font-semibold">
                  {unreadCount}
                </span>
              )}
            </NavLink>
          );
        })}
      </div>

      <div className="flex-1 overflow-auto p-6 max-w-[1100px] mx-auto w-full">
        <NotificationBanner />
        <Outlet />
      </div>
    </div>
  );
}

function NotificationBanner() {
  const { data } = useMyNotifications();
  const markRead = useMarkNotificationsRead();
  const unread = (data?.notifications ?? []).filter((n) => !n.read);
  if (unread.length === 0) return null;

  return (
    <div className="mb-5 space-y-2">
      {unread.map((n) => (
        <div
          key={n.id}
          className="rounded-lg border border-accent/30 bg-accent/5 p-3 flex items-start gap-2.5"
        >
          <Bell size={15} className="text-accent mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-semibold text-slate-900">{n.title}</div>
            <div className="text-[12px] text-slate-600 mt-0.5">{n.message}</div>
          </div>
          <button
            onClick={() => markRead.mutate([n.id])}
            disabled={markRead.isPending}
            className="p-1 hover:bg-slate-200/60 rounded-md flex-shrink-0"
            title="Marquer comme lu"
          >
            <X size={14} className="text-slate-500" />
          </button>
        </div>
      ))}
      {unread.length > 1 && (
        <button
          onClick={() => markRead.mutate(undefined)}
          disabled={markRead.isPending}
          className="text-[11.5px] text-slate-500 hover:text-slate-900 underline"
        >
          Tout marquer comme lu
        </button>
      )}
    </div>
  );
}
